import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { ButtonConfig } from '@zipari/shared-ds-util-button';
import { StatusType } from '@zipari/shared-ds-util-status';
import { checkInputsForText } from '../../design-system.helper';

export interface PoliciesCardConfig {
  attributes: any;
  costs: any;
  footer: { right: ButtonConfig; left: ButtonConfig };
  grid: {
    mobile: { row: string; column: string };
    desktop: { row: string; column: string };
  };
  productCoverages: any[];
  showOnlyCurrentPolicies: boolean;
  hideStatusBackground?: boolean;
  statusMap: { [statusKey: string]: { label: string; statusType: StatusType } };
  usePlanStatus: boolean;
}

@Component({
  selector: 'policies-card',
  templateUrl: './policies-card.component.html',
  styleUrls: ['./policies-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoliciesCardComponent implements OnInit, OnChanges {
  @Input() activePolicy: any;
  @Input() allPolicies: any[] = [];
  @Input() activeTab: string;
  @Input() activeTabs: string;
  @Input() config: PoliciesCardConfig;
  @Input() policyGroup: any;
  @Input() costs: any;
  @Input() progressBarsData: any;
  @Input() member: any;

  @Output() setActivePolicy: EventEmitter<any> = new EventEmitter<any>();

  policyStatus = null;
  policiesLabels: string[] = [];

  constructor(
    private titleCasePipe: TitleCasePipe,
    private elementRef: ElementRef,
  ) {}

  ngOnInit() {
    const [
      activePolicy,
      allPolicies,
      activeTab,
      activeTabs,
      config,
      policyGroup,
      costs,
      progressBarsData,
      member,
    ] = checkInputsForText([
      this.activePolicy,
      this.allPolicies,
      this.activeTab,
      this.activeTabs,
      this.config,
      this.policyGroup,
      this.costs,
      this.progressBarsData,
      this.member,
    ]);

    this.activePolicy = activePolicy;
    this.allPolicies = allPolicies as any[];
    this.activeTab = activeTab as string;
    this.activeTabs = activeTabs as string;
    this.config = config as PoliciesCardConfig;
    this.policyGroup = policyGroup;
    this.costs = costs;
    this.progressBarsData = progressBarsData;
    this.member = member;

    this.processAllPolicies();
    this.setPolicyStatus(this.activePolicy);

    this.elementRef.nativeElement.addEventListener('updatePolicy', (event) => {
      this.activePolicy = event.detail.policy;
      this.config = event.detail.config;

      this.setPolicyStatus(this.activePolicy);
      this.processAllPolicies();
    });
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    const activePolicyChange: SimpleChange = simpleChanges.activePolicy;

    if (activePolicyChange) {
      this.setPolicyStatus(activePolicyChange.currentValue);
    }

    if (simpleChanges.allPolicies) {
      this.processAllPolicies();
    }
  }

  private processAllPolicies() {
    if (!Array.isArray(this.allPolicies)) return;

    const [filteredPolicies] = checkInputsForText([this.allPolicies]) as any[];

    if (this.config.productCoverages) {
      const filteredPolicies = [];

      this.allPolicies.forEach((policy) => {
        if (
          this.config.productCoverages.includes(
            policy.product_coverage_type.toLowerCase(),
          )
        ) {
          filteredPolicies.push(policy);
        }
      });
    }

    this.allPolicies = filteredPolicies;
    if (!this.activePolicy && filteredPolicies.length > 0) {
      this.activePolicy = filteredPolicies[0];
    }
    this.policiesLabels = filteredPolicies
      .filter((policy) =>
        this.config.showOnlyCurrentPolicies
          ? policy._status === 'current'
          : true,
      )
      .map((policy) =>
        this.titleCasePipe.transform(policy.product_coverage_type),
      );
  }

  setPolicy(index) {
    this.setActivePolicy.emit(this.allPolicies[index]);
  }

  setPolicyStatus(activePolicy) {
    if (this.config && this.config.statusMap) {
      this.policyStatus = this.config.usePlanStatus
        ? this.getPlanStatus(activePolicy)
        : this.getProductStatus(activePolicy);
    }
  }

  private getPlanStatus(activePolicy) {
    return this.config.statusMap[activePolicy?.policy_status?.toLowerCase()];
  }

  private getProductStatus(activePolicy) {
    const statusType =
      activePolicy._status === 'past' ? StatusType.EXPIRED : StatusType.SUCCESS;

    return Object.values(this.config.statusMap).find(
      (entry) => entry.statusType === statusType,
    );
  }

  // Get the appropriate attributes based on the policy type
  getPolicyAttributes(policy) {
    if (!policy || !this.config || !this.config.attributes) {
      return [];
    }

    // Check if we have policy-specific attributes
    const policyType = policy.product_coverage_type.toLowerCase();
    const policySpecificAttributes = this.config.attributes[policyType];

    if (
      policySpecificAttributes &&
      typeof policySpecificAttributes === 'object' &&
      'attributes' in policySpecificAttributes
    ) {
      return policySpecificAttributes.attributes || [];
    }

    // Fall back to default attributes
    return this.config.attributes.attributes || [];
  }
}
